/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { DIALOG_IDS, MOMENT_INPUT_FORMATS } from 'constants';

import {
  Form,
  FormLabel,
  Select,
  SelectItem,
  TextArea,
  TextInput,
  TooltipDefinition,
} from 'carbon-addons-iot-react';
import { Formik } from 'formik';
import find from 'lodash/find';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';

import { reduxDialog } from '../../containers/ReduxDialog/ReduxDialog';

import { BaseModal, SimpleComboBoxFormik } from 'Common';
import { formatAppError } from 'Utils';

const propTypes = {
  payload: PropTypes.shape({
    /** an array of the defined service configurations of maximo */
    services: PropTypes.arrayOf(
      PropTypes.shape({
        siteDimension: PropTypes.string,
        assetDimension: PropTypes.string,
        serviceConfigId: PropTypes.number,
        username: PropTypes.string,
      })
    ).isRequired,
    /** details of the alert that we should create the workorder for */
    alert: PropTypes.shape({
      entityTypeName: PropTypes.string,
      entityId: PropTypes.string,
      deviceDto: PropTypes.shape({ name: PropTypes.string }),
      alertTypeName: PropTypes.string,
      timestamp: PropTypes.number,
    }).isRequired,
    /** an object of the available dimensions for this entityType */
    dimensions: PropTypes.objectOf(PropTypes.any),
    assets: PropTypes.arrayOf(
      PropTypes.shape({
        uuid: PropTypes.string,
        name: PropTypes.string,
        parentLocationName: PropTypes.string,
        siteName: PropTypes.string,
      })
    ),
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        uuid: PropTypes.string,
        name: PropTypes.string,
        siteName: PropTypes.string,
      })
    ),
    sites: PropTypes.arrayOf(
      PropTypes.shape({
        uuid: PropTypes.string,
        name: PropTypes.string,
      })
    ),
    dashboardLink: PropTypes.string,
    onCreate: PropTypes.func.isRequired,
  }).isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

const messages = defineMessages({
  title: 'Create service request in Manage',
  primaryButtonText: 'Create',
  assetId: 'Asset',
  assetIdPlaceholder: 'Enter asset ID (optional)',
  locationId: 'Location',
  locationIdPlaceholder: 'Enter location ID (optional)',
  siteId: 'Site',
  siteIdPlaceholder: 'Enter site (optional)',
  description: 'Summary:',
  descriptionPlaceholder: 'Provide a summary of the service request',
  reportedBy: 'Reported by:',
  reportedByPlaceholder: 'Enter user reporting',
  details: 'Details:',
  detailsPlaceholder: 'Provide details of the service request',
  required: 'Required',
  service: 'Manage service:',
  close: 'Close',
  errorCreating: 'Server error',
  descriptionText: 'Alert triggered: {name} at {time}.',
  detailsText: 'Link to dashboard: {link}',
  assetIdHelp:
    'The Asset value is populated automatically from the hierarchy if a link is found for the selected alert. If more than one possible value is found, the options are selectable from a combo box. Otherwise a free text input is provided.',
  locationIdHelp:
    'The Location value is populated automatically from the hierarchy if a link is found for the selected alert. If more than one possible value is found, the options are selectable from a combo box. Otherwise a free text input is provided.',
  siteIdHelp:
    'The Site value is populated automatically from the hierarchy if a link is found for the selected alert. Otherwise a free text input is provided.',
  maxLength: 'Exceeded maximum length {length}',
});

const CreateServiceRequestModal = ({
  intl: { formatMessage },
  payload: { services, dimensions, alert, assets, locations, sites, dashboardLink, onCreate },
  onClose,
  data__, // eslint-disable-line react/prop-types
  ...others
}) => {
  const [error, setError] = useState();

  // BaseModal can only take a single string as an error message so define the formatted error here
  // then concat it in the modal
  let errorMessage;
  if (error) {
    errorMessage = { ...formatAppError(messages.errorCreating, error, formatMessage) };
  }

  const handleWorkOrderCreation = values =>
    onCreate(values)
      .then(() => {
        // if no error is thrown close the dialog
        onClose();
      })
      .catch(errorResponse => {
        setError(errorResponse);
      });

  return (
    <Formik
      onSubmit={handleWorkOrderCreation}
      validate={values => {
        const errors = {};
        if (values.description.length > 100) {
          errors.description = formatMessage(messages.maxLength, {
            length: 100,
          });
        }

        return errors;
      }}
      enableReinitialize
      initialValues={{
        deviceId: alert?.entityId ?? alert?.deviceDto?.name,
        assetId:
          assets?.length > 0
            ? assets[0].name
            : dimensions && dimensions[services[0]?.assetDimension || 'ASSETID'],
        locationId:
          locations?.length > 0 && locations[0]?.name
            ? locations[0].name
            : assets?.length > 0 && assets[0].parentLocationName
            ? assets[0].parentLocationName
            : '',
        serviceConfigId: services[0]?.serviceConfigId,
        siteId:
          sites?.length > 0 && sites[0]?.name
            ? sites[0].name
            : locations?.length > 0 && locations[0]?.siteName
            ? locations[0].siteName
            : assets?.length > 0 && assets[0].siteName
            ? assets[0].siteName
            : dimensions && dimensions[services[0]?.siteDimension || 'SITEID'],
        description: formatMessage(messages.descriptionText, {
          name: alert?.alertTypeName,
          entity: alert?.entityId ?? alert?.deviceDto?.name,
          time: moment(alert.timestamp).format(MOMENT_INPUT_FORMATS.LONG),
        }),
        reportedBy:
          services[0]?.username !== 'apikey' &&
          services[0]?.username !== 'apiKey' &&
          !isEmpty(services[0]?.username)
            ? services[0]?.username
            : '',
        details: dashboardLink
          ? formatMessage(messages.detailsText, {
              link: dashboardLink,
            })
          : '',
      }}>
      {({
        values,
        touched,
        errors,
        isSubmitting,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
      }) => (
        <BaseModal
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...others}
          className="create-service-request-modal"
          size="sm"
          sendingData={isSubmitting}
          onClose={onClose}
          header={{
            title: formatMessage(messages.title),
          }}
          footer={{
            primaryButtonText: formatMessage(messages.primaryButtonText),
          }}
          onSubmit={handleSubmit}
          error={error ? `${errorMessage.title} - ${errorMessage.subtitle}` : null}>
          <Form onSubmit={handleSubmit}>
            <div className="modal-row bx--row">
              <div className="bx--col">
                <Select
                  id="serviceConfigId"
                  name="serviceConfigId"
                  defaultValue={values.service}
                  labelText={formatMessage(messages.service)}
                  onChange={event => {
                    // Update the assetId and siteId in case the second maximo service uses different dimensions
                    const matchingService = find(
                      services,
                      service => service.serviceConfigId.toString() === event.target.value
                    );
                    setFieldValue(
                      'reportedBy',
                      matchingService?.username !== 'apikey' &&
                        services[0]?.username !== 'apiKey' &&
                        !isEmpty(matchingService?.username)
                        ? matchingService?.username
                        : ''
                    );
                    // Only set initial value for asset from device dimension if there are no parent assets for the device
                    if (!values.assetId) {
                      setFieldValue(
                        'assetId',
                        dimensions && dimensions[matchingService?.assetDimension || 'ASSETID'],
                        false
                      );
                    }
                    if (!values.siteId) {
                      setFieldValue(
                        'siteId',
                        dimensions && dimensions[matchingService?.siteDimension || 'SITEID'],
                        false
                      );
                    }
                    handleChange(event);
                  }}>
                  {services.map(service => (
                    <SelectItem
                      text={service.name}
                      value={service.serviceConfigId}
                      key={service.serviceConfigId}
                    />
                  ))}
                </Select>
              </div>
            </div>
            <div className="modal-row bx--row">
              <div className="bx--col">
                <TextInput
                  id="reportedBy"
                  name="reportedBy"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.reportedBy || ''}
                  invalid={errors.reportedBy && touched.reportedBy}
                  invalidText={errors.reportedBy}
                  labelText={formatMessage(messages.reportedBy)}
                  placeholder={formatMessage(messages.reportedByPlaceholder)}
                />
              </div>
            </div>
            <div className="modal-row bx--row">
              <div className="bx--col">
                {assets?.length > 1 ? ( // Show a combobox if there is more than one asset
                  <SimpleComboBoxFormik
                    light
                    titleText={
                      <TooltipDefinition
                        align="start"
                        direction="bottom"
                        tooltipText={formatMessage(messages.assetIdHelp)}>
                        {formatMessage(messages.assetId)}
                      </TooltipDefinition>
                    }
                    initialSelectedItem={{ id: assets[0].uuid, text: assets[0].name }}
                    items={assets.map(asset => ({
                      ...asset,
                      id: asset.uuid,
                      text: asset.name,
                    }))}
                    onChange={selectedItem => {
                      if (selectedItem) {
                        if (locations?.length === 0) {
                          if (selectedItem.parentLocationName) {
                            setFieldValue('locationId', selectedItem.parentLocationName, false);
                          } else {
                            setFieldValue('locationId', '', false);
                          }
                        }
                        if (sites?.length === 0 && selectedItem.siteName) {
                          setFieldValue('siteId', selectedItem.siteName, false);
                        }
                      }
                    }}
                    id="assetId"
                  />
                ) : assets?.length === 1 ? ( // Show a read-only label with the asset if there is only one asset
                  <>
                    <FormLabel>
                      <TooltipDefinition
                        align="start"
                        direction="bottom"
                        tooltipText={formatMessage(messages.assetIdHelp)}>
                        {formatMessage(messages.assetId)}
                      </TooltipDefinition>
                    </FormLabel>
                    <p>{values.assetId}</p>
                  </>
                ) : locations?.length === 0 ? ( // As a fallback, show the free input text field if we also don't have any location. If we have a location but no asset, it means that there is no asset.
                  <TextInput
                    id="assetId"
                    name="assetId"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.assetId || ''}
                    invalid={errors.assetId && touched.assetId}
                    invalidText={errors.assetId}
                    labelText={
                      <TooltipDefinition
                        align="start"
                        direction="bottom"
                        tooltipText={formatMessage(messages.assetIdHelp)}>
                        {formatMessage(messages.assetId)}
                      </TooltipDefinition>
                    }
                    placeholder={formatMessage(messages.assetIdPlaceholder)}
                  />
                ) : (
                  // We can assume here that there is no asset, so no asset will be included in the Service Request
                  <>
                    <FormLabel>
                      <TooltipDefinition
                        align="start"
                        direction="bottom"
                        tooltipText={formatMessage(messages.assetIdHelp)}>
                        {formatMessage(messages.assetId)}
                      </TooltipDefinition>
                    </FormLabel>
                    <p>-</p>
                  </>
                )}
              </div>
            </div>
            <div className="modal-row bx--row">
              <div className="bx--col">
                {locations?.length > 1 ? ( // Show a combobox if there is more than one location
                  <SimpleComboBoxFormik
                    light
                    titleText={
                      <TooltipDefinition
                        align="start"
                        direction="bottom"
                        tooltipText={formatMessage(messages.locationIdHelp)}>
                        {formatMessage(messages.locationId)}
                      </TooltipDefinition>
                    }
                    initialSelectedItem={{ id: locations[0].uuid, text: locations[0].name }}
                    items={locations.map(location => ({
                      ...location,
                      id: location.uuid,
                      text: location.name,
                    }))}
                    onChange={selectedItem => {
                      if (selectedItem && sites?.length === 0 && selectedItem.siteName) {
                        setFieldValue('siteId', selectedItem.siteName, false);
                      }
                    }}
                    id="locationId"
                  />
                ) : locations?.length === 0 && assets?.length === 0 ? ( // If neither location not asset exist, let the user enter the value in a free text input
                  <TextInput
                    id="locationId"
                    name="locationId"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.locationId || ''}
                    invalid={errors.locationId && touched.locationId}
                    invalidText={errors.locationId}
                    labelText={
                      <TooltipDefinition
                        align="start"
                        direction="bottom"
                        tooltipText={formatMessage(messages.locationIdHelp)}>
                        {formatMessage(messages.locationId)}
                      </TooltipDefinition>
                    }
                    placeholder={formatMessage(messages.locationIdPlaceholder)}
                  />
                ) : (assets?.length > 0 && assets[0].parentLocationName) ||
                  (locations?.length === 1 && locations[0].name) ? ( // Location field can be populated automatically when the user changes the asset, or it can be populated from a single location passed as prop
                  <>
                    <FormLabel>
                      <TooltipDefinition
                        align="start"
                        direction="bottom"
                        tooltipText={formatMessage(messages.locationIdHelp)}>
                        {formatMessage(messages.locationId)}
                      </TooltipDefinition>
                    </FormLabel>
                    <p>{values.locationId || '-'}</p>
                  </>
                ) : (
                  // As a last resort, if the selected asset doesn have a parentLocationName and we don't have any location passed as prop, just leave the field blank
                  <>
                    <FormLabel>
                      <TooltipDefinition
                        align="start"
                        direction="bottom"
                        tooltipText={formatMessage(messages.locationIdHelp)}>
                        {formatMessage(messages.locationId)}
                      </TooltipDefinition>
                    </FormLabel>
                    <p>-</p>
                  </>
                )}
              </div>
            </div>
            <div className="modal-row bx--row">
              <div className="bx--col">
                {sites?.length > 1 ? (
                  <SimpleComboBoxFormik
                    light
                    titleText={
                      <TooltipDefinition
                        align="start"
                        direction="bottom"
                        tooltipText={formatMessage(messages.siteIdHelp)}>
                        {formatMessage(messages.siteId)}
                      </TooltipDefinition>
                    }
                    initialSelectedItem={{ id: sites[0].uuid, text: sites[0].name }}
                    items={locations.map(site => ({
                      ...site,
                      id: site.uuid,
                      text: sites.name,
                    }))}
                    id="siteId"
                  />
                ) : sites?.length === 0 && locations?.length === 0 && assets?.length === 0 ? ( // Free text input only if we don't have asset, location and site, otherwise the site can be derived from asset or location
                  <TextInput
                    id="siteId"
                    name="siteId"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.siteId || ''}
                    invalid={errors.siteId && touched.siteId}
                    invalidText={errors.locationId}
                    labelText={
                      <TooltipDefinition
                        align="start"
                        direction="bottom"
                        tooltipText={formatMessage(messages.siteIdHelp)}>
                        {formatMessage(messages.siteId)}
                      </TooltipDefinition>
                    }
                    placeholder={formatMessage(messages.siteIdPlaceholder)}
                  />
                ) : (
                  // Show site derived from either asset or location (check logic in initialValues)
                  <>
                    <FormLabel>
                      <TooltipDefinition
                        align="start"
                        direction="bottom"
                        tooltipText={formatMessage(messages.siteIdHelp)}>
                        {formatMessage(messages.siteId)}
                      </TooltipDefinition>
                    </FormLabel>
                    <p>{values.siteId}</p>
                  </>
                )}
              </div>
            </div>
            <div className="modal-row bx--row">
              <div className="bx--col">
                <TextInput
                  id="description"
                  name="description"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.description || ''}
                  invalid={errors.description && touched.description}
                  invalidText={errors.description}
                  labelText={formatMessage(messages.description)}
                  placeholder={formatMessage(messages.descriptionPlaceholder)}
                />
              </div>
            </div>
            <div className="modal-row bx--row">
              <div className="bx--col">
                <TextArea
                  id="details"
                  name="details"
                  cols={100}
                  rows={10}
                  value={values.details || ''}
                  labelText={formatMessage(messages.details)}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={errors.details && touched.details}
                  invalidText={errors.details}
                  placeholder={formatMessage(messages.detailsPlaceholder)}
                />
              </div>
            </div>
          </Form>
        </BaseModal>
      )}
    </Formik>
  );
};

CreateServiceRequestModal.propTypes = propTypes;
CreateServiceRequestModal.defaultProps = {
  isOpen: false,
};

export default injectIntl(
  reduxDialog({ name: DIALOG_IDS.CREATE_WORKORDER_MODAL })(CreateServiceRequestModal)
);
